import React, { useEffect, useState } from 'react';
import PositionDrawerHeaderFooter from '../job/postionDrawer/PositionDrawerHeaderFooter';
import PositionDrawerBody from '../job/postionDrawer/PositionDrawerBody';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { ApplicationStatus, UIPosition, UIUser } from 'types';
import { Drawer } from '../shared/Drawer';
import { Loader } from 'ui';
import Container from '../shared/Container';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';

interface Props {
	isPositionDrawerOpen: boolean;
	positionId: string;
	setIsPositionDrawerOpen: (val: boolean) => void;
	closeDrawer: () => void;
}

export interface PositionSkill {
	id: string;
	name: string;
	threshold: number;
	rating: number;
}

const RecruiterPositionDrawer = ({ isPositionDrawerOpen, closeDrawer, positionId, setIsPositionDrawerOpen }: Props) => {
	const user = useUserContext();
	const [isUserAlreadyApplied, setIsUserAlreadyApplied] = useState<boolean | null>(null);
	const [positionShouldBeUnlocked, setPositionShouldBeUnlocked] = useState<boolean>(false);

	// Fetching Position data by Position Id and setting isUserAlreadyApplied state
	const { data, isLoading: isPositionByIdDataLoading } = useGetPositionByIdQuery(positionId, data => {
		const isUserApplied =
			data.applications?.some(
				application =>
					![ApplicationStatus.OPENED_BY_EMPLOYER, ApplicationStatus.OPENED_BY_ADMIN].includes(application.status),
			) ?? false;

		setIsUserAlreadyApplied(isUserApplied);
	});

	const positionSkills =
		data?.position?.positionRequirement.skillThresholds &&
		data.position?.positionRequirement.skillThresholds
			.filter(pst => pst.threshold > 0)
			.map(pst => {
				return {
					id: pst.displaySkill,
					name: pst.displaySkill,
					threshold: pst.threshold,
					rating: 0,
				};
			});

	const numberOfSkillsBelowThreshold = positionSkills?.filter(s => s.rating < s.threshold).length;

	useEffect(() => {
		if (data?.applications) {
			const isUserApplied =
				data.applications?.some(
					application =>
						application.user._id === user.user?._id &&
						![ApplicationStatus.OPENED_BY_EMPLOYER, ApplicationStatus.OPENED_BY_ADMIN].includes(application.status),
				) ?? false;

			setIsUserAlreadyApplied(isUserApplied);
			const shouldBeUnlocked =
				data.applications?.some(
					application =>
						application.user._id === user.user?._id &&
						[ApplicationStatus.OPENED_BY_EMPLOYER, ApplicationStatus.OPENED_BY_ADMIN].includes(application.status),
				) ?? false;
			setPositionShouldBeUnlocked(shouldBeUnlocked);
		}
	}, [data?.applications, isPositionDrawerOpen, user.user?._id]);

	return (
		<Drawer
			isOpen={isPositionDrawerOpen}
			onClose={() => {
				setIsUserAlreadyApplied(null);
				setIsPositionDrawerOpen(false);
			}}
			header={
				<>
					{isPositionByIdDataLoading || !data?.position ? (
						<div className="mt-8">
							{/* <Loader /> */}
							<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="w-[20%] mx-auto" />
						</div>
					) : (
						<Container className="!px-0 xl:!px-[80px]">
							<PositionDrawerHeaderFooter
								setIsPositionDrawerOpen={setIsPositionDrawerOpen}
								positionDetail={data?.position as UIPosition}
								positionRecommendations={data?.recommendations}
								isFooter={false}
								shouldBeUnlocked={positionShouldBeUnlocked}
								numberOfSkillsBelowThreshold={numberOfSkillsBelowThreshold || 0}
								userDetail={user.user as UIUser}
								isUserAlreadyApplied={isUserAlreadyApplied}
								closeDrawer={closeDrawer}
							/>
							<div className="flex justify-center">
								<hr className="h-[1px] w-full rounded-[32px] border-0 bg-[#C7C9D9] dark:bg-[#8F90A6]" />
							</div>
						</Container>
					)}
				</>
			}
			body={
				<>
					<Container>
						{isPositionByIdDataLoading || !data?.position ? (
							<div />
						) : (
							<PositionDrawerBody
								positionSkills={positionSkills as PositionSkill[]}
								positionDetail={data?.position as UIPosition}
								userDetail={user?.user as UIUser}
							/>
						)}
					</Container>
				</>
			}
			footer={
				<>
					{isPositionByIdDataLoading || !data?.position ? (
						<div />
					) : (
						<div
							style={{
								filter: 'drop-shadow(0px -4px 4px rgba(49, 49, 49, 0.25))',
							}}
							className="bg-[#E5F0FF] dark:bg-[#111E2C] md:hidden"
						>
							<PositionDrawerHeaderFooter
								positionDetail={data?.position as UIPosition}
								positionRecommendations={data?.recommendations}
								isFooter={true}
								shouldBeUnlocked={positionShouldBeUnlocked}
								numberOfSkillsBelowThreshold={numberOfSkillsBelowThreshold || 0}
								userDetail={user.user as UIUser}
								isUserAlreadyApplied={isUserAlreadyApplied}
								closeDrawer={closeDrawer}
								setIsPositionDrawerOpen={setIsPositionDrawerOpen}
							/>
						</div>
					)}
				</>
			}
		/>
	);
};

export default RecruiterPositionDrawer;
